import { getAgentColor } from '../lib/agentColor';

/**
 * A small triangular prism glyph. Ported verbatim from Simpliloquy V2.
 *
 * Used to mark each agent with its frequency. Two facets: a base triangle in
 * the agent's accent and a left facet rendered slightly darker so the shape
 * reads as a physical wedge of glass catching light, not a flat triangle.
 */
interface Props {
  slug: string;
  size?: number;
  active?: boolean;
  className?: string;
}

export function Prism({ slug, size = 14, active = false, className }: Props) {
  const { hue, chroma } = getAgentColor(slug);
  const c = (base: number): string => (base * chroma).toFixed(3);

  const light = `oklch(78% ${c(0.14)} ${hue})`;
  const mid = `oklch(66% ${c(0.13)} ${hue})`;
  const dark = `oklch(48% ${c(0.09)} ${hue})`;
  const edge = `oklch(86% ${c(0.1)} ${hue} / 0.55)`;

  // Apex up, base at bottom. Left facet gets the darker fill so light
  // reads as entering from the right. Slight base blur suggests the
  // glass sitting on a surface.
  const pad = 1;
  const apex = `${size / 2},${pad}`;
  const baseLeft = `${pad},${size - pad}`;
  const baseRight = `${size - pad},${size - pad}`;
  const midBottom = `${size / 2},${size - pad}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={className}
      aria-hidden="true"
      style={{
        filter: active
          ? `drop-shadow(0 0 3px oklch(72% ${c(0.18)} ${hue} / 0.55))`
          : undefined,
      }}
    >
      {/* full triangle (right-facet tone, catches light) */}
      <polygon points={`${apex} ${baseRight} ${baseLeft}`} fill={mid} />
      {/* left facet, slightly darker, so the prism reads as 3D */}
      <polygon points={`${apex} ${midBottom} ${baseLeft}`} fill={dark} />
      {/* highlight along right edge */}
      <line
        x1={size / 2}
        y1={pad}
        x2={size - pad}
        y2={size - pad}
        stroke={active ? light : edge}
        strokeWidth={active ? 1 : 0.75}
        strokeLinecap="round"
      />
    </svg>
  );
}
